import React, { useCallback, useMemo } from "react";
import "./Setting.css";
import { Grid, useMediaQuery, Typography, Box, useTheme } from "@mui/material";
import Confirm from "./Confirm";
import { useSelector } from "react-redux";
import ChangePassword from "./ChangePassword";
import SettingsActionCard from "./SettingsActionCard";
import { 
  Key as KeyIcon, 
  Visibility as VisibilityIcon, 
  VisibilityOff as VisibilityOffIcon,
  DeleteForever as DeleteIcon 
} from "@mui/icons-material";

const Setting = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 1020px)");
  const { status } = useSelector((state) => state.profile.user);
  const { profileDisabled, isGuest } = useSelector((state) => state.main);
  
  // Create refs for the dialog components
  const deleteAccountRef = React.useRef();
  const changePasswordRef = React.useRef();
  const toggleProfileRef = React.useRef();

  const handleDeleteAccount = useCallback(() => {
    deleteAccountRef.current?.handleClickOpen();
  }, []);

  const handleChangePassword = useCallback(() => {
    changePasswordRef.current?.handleClickOpen();
  }, []);

  const handleToggleProfile = useCallback(() => {
    toggleProfileRef.current?.handleClickOpen();
  }, []);

  // Move useMemo to component level to avoid conditional hook calls
  const statusChip = useMemo(() => {
    // Only show status chip if profile is actually live (status === 1 and not disabled)
    if (status === 1 && !profileDisabled) {
      return { label: "Live", color: "success" };
    } else if (profileDisabled) {
      return { label: "Hidden", color: "warning" };
    } else {
      // Profile is in draft or not created - don't show misleading status
      return null;
    }
  }, [status, profileDisabled]);

  return (
    <Box sx={{ px: 2 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        Account Settings
      </Typography>
      
      <Grid container spacing={3} sx={{ pb: 2, pt: 1 }}>
        {/* Update Password Card */}
        <Grid item xs={12} md={4}>
          <SettingsActionCard
            icon={<KeyIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />}
            title="Update Password"
            description="Change your account password. Minimum 8 characters required."
            buttonText="Update Password"
            onClick={handleChangePassword}
          />
        </Grid>

        {/* Profile Visibility Card - Only show for tutors */}
        {!isGuest && (status === 0 || status === 1) && (
          <Grid item xs={12} md={4}>
            <SettingsActionCard
              icon={profileDisabled ? <VisibilityOffIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} /> : <VisibilityIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />}
              title={profileDisabled ? "Enable Profile" : "Disable Profile"}
              description={profileDisabled 
                ? "Make your profile visible in search results again." 
                : "Hide your profile from student search, but keep your account active."
              }
              buttonText={profileDisabled ? "Enable Profile" : "Disable Profile"}
              onClick={handleToggleProfile}
              statusChip={statusChip}
            />
          </Grid>
        )}

        {/* Delete Account Card */}
        <Grid item xs={12} md={4}>
          <SettingsActionCard
            icon={<DeleteIcon sx={{ color: '#d32f2f', fontSize: 24 }} />}
            title="Delete Account"
            description="Permanently delete your account and all associated data. This action cannot be undone."
            buttonText="Delete Account"
            onClick={handleDeleteAccount}
            destructive={true}
            buttonColor="error"
          />
        </Grid>
      </Grid>

      {/* Hidden dialog components */}
      <Confirm disable={false} isMobile={isMobile} ref={deleteAccountRef} />
      <ChangePassword isMobile={isMobile} ref={changePasswordRef} />
      {!isGuest && (status === 0 || status === 1) && <Confirm disable={true} isMobile={isMobile} ref={toggleProfileRef} />}
    </Box>
  );
});

Setting.displayName = 'Setting';

export default Setting;
